import Axios from "../config/axios-v1-config";
import { apiBodyMessages, apiErrorMessage } from "../utils/apiErrorMessage";

// Equipo INTERNO de la empresa logística (back-office del operador:
// administrator, superadministrator, supervisor, support). Rutas homeadas en
// microservice-users bajo /users/logistic-team/*; el logisticCompanyId lo deriva
// el backend del JWT (no se manda desde acá).
//
// La verificación y aceptación de la invitación son comunes con el equipo de
// comercio y viven en api/team.js (verifyTeamInvite / acceptTeamInvite): el
// token es el mismo mecanismo y la página pública de aceptación es la misma.

/**
 * Roles que el operador puede asignar al invitar. Debe calzar con
 * INVITABLE_LOGISTIC_ROLES del backend, que rechaza cualquier otro.
 *
 * "Supervisor" está fuera a propósito: el rol nunca se probó en producción, así
 * que no se ofrece hasta validarlo. Los supervisores que ya existen se siguen
 * listando y gestionando con normalidad; lo único apagado es crear nuevos.
 * Para rehabilitarlo hay que agregarlo acá Y en el backend.
 */
export const INVITABLE_LOGISTIC_ROLES = [
  { value: "administrator", label: "Administrador", hint: "Acceso total al panel del operador" },
  { value: "support", label: "Soporte", hint: "Mesa de ayuda, incidencias y alertas" },
];

/**
 * Nivel de cada rol, espejo de logistic-team.constants.ts del backend. Se usa
 * para no ofrecer en el selector roles que el backend rechazaría con 403.
 *
 * `supervisory` es el nombre real del supervisor en la BD (name_public
 * "Supervisor"); se contempla para que el rango calce con lo que llega del API.
 */
const ROLE_RANK = {
  support: 1,
  supervisor: 1,
  supervisory: 1,
  administrator: 2,
  superadministrator: 3,
};

/** Únicos roles que pueden gestionar el equipo interno. Debe calzar con el backend. */
const MANAGER_ROLES = ["administrator", "superadministrator"];

/**
 * Roles asignables por quien tiene `callerRoleName`. Vacío si no es gestor.
 *
 * El chequeo de gestor va PRIMERO y no se deduce del rango: un supervisor tiene
 * rango suficiente para "supervisor" y "soporte", así que sin este guard se le
 * ofrecería invitar y el backend lo rechazaría con 403.
 */
export const invitableRolesFor = (callerRoleName) => {
  if (!MANAGER_ROLES.includes(callerRoleName)) return [];
  const rank = ROLE_RANK[callerRoleName] ?? 0;
  return INVITABLE_LOGISTIC_ROLES.filter((r) => ROLE_RANK[r.value] <= rank);
};

export const getLogisticTeam = async () => {
  try {
    const response = await Axios.get("/users/logistic-team");
    return { data: response.data, error: null };
  } catch (error) {
    return { data: null, error: error.response?.data || { message: "Error al obtener el equipo interno" } };
  }
};

/**
 * Error del backend en el shape que consumen las vistas del equipo interno.
 *
 * El alta pasa por el ValidationPipe del backend, que contesta 400 con
 * `message` como ARRAY de restricciones (una por campo) y 409 con
 * `{message, field}`. El body se conserva tal cual (así `field` sigue
 * llegando igual) y se le agrega:
 *   · message  → texto legible, con el array ya unido por ' · '. Es null
 *     cuando el body no trae nada mostrable (403/500 genéricos, error de
 *     red): un literal en español acá haría inalcanzable el fallback
 *     TRADUCIDO de la vista, que es quien decide con qué texto reemplazarlo.
 *   · messages → el array crudo, que es lo que permite marcar cada input
 * Lo usan también activar/desactivar/reenviar, para que sus toasts lean el
 * mismo `message` normalizado.
 */
const normalizeTeamError = (error) => {
  const body = error?.response?.data;
  const base = body && typeof body === "object" && !Array.isArray(body) ? body : {};
  return {
    ...base,
    message: apiErrorMessage(error),
    messages: apiBodyMessages(base),
  };
};

export const createLogisticTeamUser = async (userData) => {
  try {
    const response = await Axios.post("/users/logistic-team", userData);
    return { data: response.data, error: null };
  } catch (error) {
    // El contrato {data, error} no cambia: esta función nunca lanza.
    return { data: null, error: normalizeTeamError(error) };
  }
};

export const activateLogisticTeamUser = async (userId) => {
  try {
    const response = await Axios.patch(`/users/logistic-team/${userId}/activate`);
    return { data: response.data, error: null };
  } catch (error) {
    return { data: null, error: normalizeTeamError(error) };
  }
};

export const deactivateLogisticTeamUser = async (userId) => {
  try {
    const response = await Axios.patch(`/users/logistic-team/${userId}/deactivate`);
    return { data: response.data, error: null };
  } catch (error) {
    return { data: null, error: normalizeTeamError(error) };
  }
};

export const resendLogisticTeamInvite = async (userId) => {
  try {
    const response = await Axios.post(`/users/logistic-team/${userId}/resend`);
    return { data: response.data, error: null };
  } catch (error) {
    return { data: null, error: normalizeTeamError(error) };
  }
};
